import React, { useState, useEffect, useMemo, useCallback, useImperativeHandle, useRef, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import ActivityModal from './ActivityModal';
import ItineraryDragDropProvider, { ItineraryDayDragArea } from './ItineraryDayDragDrop';
import RoadTripPlanner from './RoadTripPlanner';
import RoadTripMapModal from './RoadTripMapModal';
import ItineraryStop from './ItineraryStop';
import ItineraryReservations from './ItineraryReservations';
import ItemDetailModal from './ItemDetailModal';
import DocumentSectionHead from './DocumentSectionHead';
import { isMobileApp, openAIConciergeinBrowser } from '../utils/platformDetection';
import { getCurrencySymbol } from '../utils/currencyUtils';
import {
  parseCompositeItineraryId,
  resolveFlightJourneyItems,
  buildFlightLegUpdatePayload,
  buildFlightJourneyEditItem,
  groupFlightsIntoJourneys,
  getJourneyCityRouteLabel,
} from '../utils/flightJourneyUtils';
import { isFlightItineraryItem } from '../utils/flightRouteUtils';
import { isTripEligibleForFlightStatus } from '../utils/liveFlightStatus';
import { lookupFlightLegs } from '../utils/flightLegLookupUtils';
import { getRoadTripCardTitles } from '../utils/roadTripDragUtils';
import { isItineraryItemOnDay } from '../utils/itineraryDaySpan';
import { DOCUMENT, documentSubTabsSx, documentToggleSx, documentTextButtonSx, documentOutlineButtonSx, documentOutlineIconButtonSx, documentTabIndicatorSx } from '../constants/documentTheme';
import { PaidFeatureLabel } from './DocumentProBadge';
import { dayHasTransit, formatTime24, getDayTimeSpan, getEmptyDayStatusKey, isReservationOnlyItem } from '../utils/itineraryDocumentUtils';
import { canEditSharedItinerary, canManageItineraryDrafts, isReadOnlyGroupPlanItem, visibleItineraryForViewer } from '../utils/itineraryVisibility';
import { canEditTripItinerary, isManagedByPartner, showPaidAiBadge } from '../utils/tripUtils';
import TripPackageAddOns from './TripPackageAddOns';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Collapse,
  Container,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarTodayIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Hotel as HotelIcon,
  DirectionsCar as DirectionsCarIcon,
  Flight as FlightIcon,
  Event as EventIcon,
  Route as RouteIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { format, differenceInDays, addDays, isToday, startOfDay } from 'date-fns';
import { zhCN, de, fr, es, it, ar } from 'date-fns/locale';
import { useItinerary } from '../hooks/useItinerary';
import { useAuth } from '../contexts/AuthContext';
import TripSuggestions from './TripSuggestions';
import TopRatedSection from './TopRatedSection';
import { useGlobalWebSocket } from '../contexts/GlobalWebSocketContext';
import ItineraryImportDialog from './ItineraryImportDialog';
import ICSImportDialog from './ICSImportDialog';
import EmailImportDialog from './EmailImportDialog';
import MyItinerary from './MyItinerary';
import PublishGuideDialog from './PublishGuideDialog';
import api from '../config/api';
import { SingleImageUploader } from './ImageUploader';
import { getDayDateKey, recalculateDayItemTimes } from '../utils/itineraryDragUtils';

const TripMapDrawer = lazy(() => import('./TripMap/TripMapDrawer'));

// Helper function to generate Google Maps link
const generateGoogleMapsLink = (location) => {
  if (!location) return null;
  const encodedLocation = encodeURIComponent(location);
  return `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
};

// Partners review suggestions only on group trips, where travelers suggest to the organizer.
const showsSuggestionsTab = ({ isPartner = false, trip } = {}) => (
  !isPartner || Boolean(Number(trip?.is_master_trip))
);

const resolveItineraryTab = (tab, { isPartner = false, trip } = {}) => {
  if (tab === 1 || tab === 'myItinerary') return 'myItinerary';
  if (tab === 'addons') return 'addons';
  if (tab === 3 || tab === 'topRated') return 'topRated';
  if (tab === 2 || tab === 'suggestions') return showsSuggestionsTab({ isPartner, trip }) ? 'suggestions' : 'topRated';
  return 'itinerary';
};

const TripItinerary = React.forwardRef(({ tripId, isCreator, isPartner, trip, partnerEntitlements, initialTab = 0, onNavigateToSuggestions, onNavigateToExplore, onNavigateToAIConcierge, onSuggestWithAi, onItineraryChange, onItemExpand }, ref) => {
  /* eslint-disable react-hooks/exhaustive-deps */
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  
  // Map i18n language codes to date-fns locales
  const getDateLocale = () => {
    const localeMap = {
      'zh-CN': zhCN,
      'zh-TW': zhCN,
      'de': de,
      'fr': fr,
      'es': es,
      'it': it,
      'ar': ar,
      'en': undefined // English is default
    };
    return localeMap[i18n.language] || undefined;
  };
  const dateLocale = getDateLocale();
  const { addEventListener, subscribeToTrip, unsubscribeFromTrip } = useGlobalWebSocket();
  const [publishGuideOpen, setPublishGuideOpen] = useState(false);
  const {
    itinerary,
    loading,
    error,
    fetchItinerary,
    addItineraryItem,
    updateItineraryItem,
    deleteItineraryItem,
    setItemVisibility,
    publishAllDrafts,
    addNote,
  } = useItinerary(tripId, onItineraryChange, { usePartnerAuth: Boolean(isPartner) });
  const canManageDrafts = canManageItineraryDrafts({ isPartner, isCreator, trip });
  const canMutateItinerary = canEditTripItinerary(trip);
  const canEditMasterItinerary = canMutateItinerary && canEditSharedItinerary({
    isPartner,
    isCreator,
    trip,
  });
  const displayItinerary = useMemo(
    () => visibleItineraryForViewer(itinerary, { isPartner, isCreator, trip }),
    [itinerary, isPartner, isCreator, trip]
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    google_map_link: '',
    category: 'other',
    start_datetime: '',
    end_datetime: '',
    description: '',
    image_url: '',
    cost: '',
    transportation_type: '',
    transportation_data: {}
  });
  const showPackageAddOns = Boolean(!isPartner && isManagedByPartner(trip));
  const showSuggestions = showsSuggestionsTab({ isPartner, trip });
  const canDragItineraryItem = useCallback(
    (item) => !isReadOnlyGroupPlanItem(item, { isPartner }),
    [isPartner]
  );
  const [currentTab, setCurrentTab] = useState(() => {
    const next = resolveItineraryTab(initialTab, { isPartner, trip });
    return next === 'addons' && !isManagedByPartner(trip) ? 'itinerary' : next;
  });
  const [expandedDays, setExpandedDays] = useState({});
  const [highlightStopIds, setHighlightStopIds] = useState([]);
  const [focusCreatedItems, setFocusCreatedItems] = useState(null);
  const appliedFocusKey = useRef('');
  const [noteDialogs, setNoteDialogs] = useState({});
  const [newNote, setNewNote] = useState('');
  const [expandCollapseMode, setExpandCollapseMode] = useState('expand');
  const [mapDrawerOpen, setMapDrawerOpen] = useState(false);
  const [mapDrawerMode, setMapDrawerMode] = useState('view'); // view | plan
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [icsImportDialogOpen, setIcsImportDialogOpen] = useState(false);
  const [emailImportOpen, setEmailImportOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isReorderingItinerary, setIsReorderingItinerary] = useState(false);
  const [optimizeDayOpen, setOptimizeDayOpen] = useState(false);
  const [optimizeDayLoading, setOptimizeDayLoading] = useState(false);
  const [optimizeDayApplying, setOptimizeDayApplying] = useState(false);
  const [optimizeDayError, setOptimizeDayError] = useState('');
  const [optimizeDayProposal, setOptimizeDayProposal] = useState(null);
  const [optimizeDayKey, setOptimizeDayKey] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openSuggestionDialog, setOpenSuggestionDialog] = useState(null);
  const openSuggestionDialogRef = useRef(null);
  const [pendingSuggestionData, setPendingSuggestionData] = useState(null);
  const [expandedStopIds, setExpandedStopIds] = useState({});
  const [roadTripMapOpen, setRoadTripMapOpen] = useState(false);
  const [roadTripMapData, setRoadTripMapData] = useState(null);
  const [roadTripMapItemName, setRoadTripMapItemName] = useState('');
  const [roadTripMapTripId, setRoadTripMapTripId] = useState(null);
  const [roadTripMapReadOnly, setRoadTripMapReadOnly] = useState(false);
  const [roadTripPlannerOpen, setRoadTripPlannerOpen] = useState(false);
  const [roadTripAnchorDate, setRoadTripAnchorDate] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [movePersonalOpen, setMovePersonalOpen] = useState(false);
  const [movePersonalItem, setMovePersonalItem] = useState(null);
  const [movePersonalUserId, setMovePersonalUserId] = useState('');
  const [movePersonalMembers, setMovePersonalMembers] = useState([]);
  const [movePersonalBusy, setMovePersonalBusy] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [photoItem, setPhotoItem] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    openAddActivityDialog: (activityData) => {
      if (!canMutateItinerary) return;
      setEditingItem(null);
      setFormData({
        name: activityData?.name || '',
        location: activityData?.location || '',
        google_map_link: activityData?.google_map_link || '',
        category: activityData?.category || 'other',
        start_datetime: activityData?.start_datetime || '',
        end_datetime: activityData?.end_datetime || '',
        description: activityData?.description || '',
        image_url: activityData?.image_url || '',
        cost: activityData?.cost || '',
        transportation_type: activityData?.transportation_type || '',
        transportation_data: activityData?.transportation_data || {}
      });
      setDialogOpen(true);
    },
    openSuggestionFromExplore: (activityData) => {
      setCurrentTab('suggestions');
      if (openSuggestionDialogRef.current) {
        setTimeout(() => openSuggestionDialogRef.current(activityData), 150);
      } else {
        setPendingSuggestionData(activityData);
      }
    },
    refreshItinerary: () => fetchItinerary(),
    focusDrafts: (created = []) => {
      appliedFocusKey.current = '';
      setCurrentTab('itinerary');
      setFocusCreatedItems(Array.isArray(created) ? created : []);
    },
    addPhotoItem: async (imageUrl) => {
      if (!imageUrl) return null;
      const start = trip?.start_date
        ? `${String(trip.start_date).slice(0, 10)}T12:00:00`
        : new Date().toISOString().slice(0, 19);
      const created = await addItineraryItem({
        name: t('partner.opsHome.quickTypes.photo', 'Photo'),
        category: 'other',
        description: '',
        location: '',
        image_url: imageUrl,
        start_datetime: start,
        end_datetime: start,
      });
      setCurrentTab('itinerary');
      if (created) setFocusCreatedItems([created]);
      return created;
    },
  }));

  useEffect(() => {
    fetchItinerary();
  }, [fetchItinerary]);

  // Keep ref in sync so useImperativeHandle always has the latest function
  useEffect(() => {
    openSuggestionDialogRef.current = openSuggestionDialog;
  }, [openSuggestionDialog]);

  // Open pending suggestion dialog once the function is available
  useEffect(() => {
    if (pendingSuggestionData && openSuggestionDialog) {
      openSuggestionDialog(pendingSuggestionData);
      setPendingSuggestionData(null);
    }
  }, [pendingSuggestionData, openSuggestionDialog]);

  useEffect(() => {
    let next = resolveItineraryTab(initialTab, { isPartner, trip });
    if (next === 'addons' && !isManagedByPartner(trip)) next = 'itinerary';
    if (next !== 'itinerary') {
      setCurrentTab(next);
    }
  }, [initialTab, isPartner, trip]);

  // Subscribe to trip for real-time updates
  useEffect(() => {
    if (subscribeToTrip) {
      subscribeToTrip(tripId);
    }
    return () => {
      if (unsubscribeFromTrip) {
        unsubscribeFromTrip(tripId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]); // Only depend on tripId to prevent infinite subscription loops

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!addEventListener) return;

    const handleItineraryEvent = (data) => {
      if (data.trip_id === tripId) {
        fetchItinerary();
      }
    };

    const unsubscribers = [
      addEventListener('itinerary_added', handleItineraryEvent),
      addEventListener('itinerary_updated', handleItineraryEvent),
      addEventListener('itinerary_deleted', handleItineraryEvent),
      addEventListener('itinerary_note_added', handleItineraryEvent),
      addEventListener('itinerary_note_deleted', handleItineraryEvent),
      addEventListener('suggestion_converted', handleItineraryEvent),
    ];

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [addEventListener, tripId, fetchItinerary]);

  // Group itinerary items by days (excluding Trip Overview items)
  const itineraryByDays = useMemo(() => {
    if (!trip || !trip.start_date || !trip.end_date) {
      return [];
    }

    // Always use trip start and end dates for day accordions
    const tripStartDate = new Date(trip.start_date.split('T')[0] + 'T00:00:00');
    const tripEndDate = new Date(trip.end_date.split('T')[0] + 'T23:45:59');
    const totalDays = differenceInDays(tripEndDate, tripStartDate) + 1;

    // A flight journey can be stored as multiple itinerary items (one per leg).
    // Map each of those item ids to its journey so the day-by-day spine can show
    // one consolidated card with every leg instead of separate one-leg cards.
    const multiItemFlightJourneys = groupFlightsIntoJourneys(
      displayItinerary.filter(isFlightItineraryItem)
    ).filter((journey) => journey.items.length > 1);
    const journeyByItemId = new Map();
    multiItemFlightJourneys.forEach((journey) => {
      journey.items.forEach((journeyItem) => {
        journeyByItemId.set(String(journeyItem.id), journey);
      });
    });

    const days = [];
    for (let i = 0; i < totalDays; i++) {
      const currentDate = addDays(tripStartDate, i);
      const dayItems = displayItinerary.filter(item => {
        if (isReservationOnlyItem(item)) return false;
        return isItineraryItemOnDay(item, currentDate);
      }).sort((a, b) => {
        // Sort by start time, handling null/undefined values
        const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
        const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
        
        // If times are equal (or both null), maintain original order
        if (timeA === timeB) return 0;
        
        // Items with no time go to the end
        if (timeA === 0) return 1;
        if (timeB === 0) return -1;
        
        // Sort by time ascending
        return timeA - timeB;
      });

      // Consolidate road trip items: group items that share the same road_trip_data
      const consolidatedItems = [];
      const seenRoadTripKeys = new Set();
      const seenFlightJourneyKeys = new Set();

      for (const item of dayItems) {
        const rtd = item.transportation_data?.road_trip_data;
        const flightJourney = journeyByItemId.get(String(item.id));

        if (rtd && (rtd.orderedStops || rtd.encodedPolyline || rtd.groupId)) {
          // Use groupId if available, fall back to encodedPolyline or orderedStops fingerprint
          const groupKey = rtd.groupId
            || rtd.encodedPolyline
            || rtd.orderedStops.map(s => s.name).join('|');

          if (seenRoadTripKeys.has(groupKey)) continue; // already consolidated
          seenRoadTripKeys.add(groupKey);

          // Collect all items in this group for this day
          const groupItems = dayItems.filter(it => {
            const itRtd = it.transportation_data?.road_trip_data;
            if (!itRtd) return false;
            const itKey = itRtd.groupId
              || itRtd.encodedPolyline
              || (itRtd.orderedStops ? itRtd.orderedStops.map(s => s.name).join('|') : null);
            return itKey === groupKey;
          });
          const mainStops = groupItems.filter(it => !it.transportation_data?.parentStop);
          const poiItems = groupItems.filter(it => it.transportation_data?.parentStop);

          const { dayTitle, tripTitle, segmentNumber, totalDays } = getRoadTripCardTitles(rtd, t);
          consolidatedItems.push({
            ...groupItems[0], // inherit id, start_datetime, etc. from first item
            _isRoadTripCard: true,
            _roadTripGroupId: rtd.groupId || groupKey,
            _roadTripData: rtd,
            _roadTripStops: mainStops,
            _roadTripPois: poiItems,
            _roadTripTitle: dayTitle,
            _roadTripTripTitle: tripTitle,
            _roadTripSegment: totalDays > 1 ? { number: segmentNumber, total: totalDays } : null,
            name: dayTitle,
            category: 'transport',
          });
        } else if (flightJourney) {
          if (seenFlightJourneyKeys.has(flightJourney.id)) continue;
          seenFlightJourneyKeys.add(flightJourney.id);

          const firstFlight = flightJourney.items[0];
          consolidatedItems.push({
            ...firstFlight,
            id: flightJourney.id,
            _flightJourney: { items: flightJourney.items },
            name: getJourneyCityRouteLabel(flightJourney) || firstFlight.name,
            start_datetime: flightJourney.start_datetime || firstFlight.start_datetime,
            end_datetime: flightJourney.end_datetime || firstFlight.end_datetime,
            category: firstFlight.category,
            transportation_type: 'flight',
            transportation_data: {
              ...(firstFlight.transportation_data || {}),
              legs: flightJourney.legs,
            },
          });
        } else {
          consolidatedItems.push(item);
        }
      }

      days.push({
        dayNumber: i + 1,
        date: currentDate,
        items: consolidatedItems,
        isToday: isToday(currentDate)
      });
    }
    return days;
  }, [displayItinerary, trip, t]);

  const plannedStopCount = useMemo(
    () => itineraryByDays.reduce((count, day) => count + day.items.length, 0),
    [itineraryByDays]
  );

  useEffect(() => {
    if (!focusCreatedItems?.length || !itineraryByDays.length) return;
    const focusKey = focusCreatedItems.map((item) => item?.id || item?.name).join(',');
    if (appliedFocusKey.current === focusKey) return;
    const ids = new Set(focusCreatedItems.map((item) => item?.id).filter((id) => id != null));
    const matchesItem = (item) => ids.has(item.id);
    const targetDays = itineraryByDays
      .filter((day) => day.items.some(matchesItem))
      .map((day) => day.dayNumber);
    if (!targetDays.length) return;

    appliedFocusKey.current = focusKey;
    setExpandedDays((prev) => {
      const next = { ...prev };
      itineraryByDays.forEach((day) => {
        next[day.dayNumber] = targetDays.includes(day.dayNumber);
      });
      return next;
    });
    const highlightIds = itineraryByDays.flatMap((day) => (
      day.items.filter(matchesItem).map((item) => item.id)
    ));
    setHighlightStopIds(highlightIds);
    const firstId = highlightIds[0];
    window.setTimeout(() => {
      document.getElementById(`itinerary-stop-${firstId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 200);
    window.setTimeout(() => setHighlightStopIds([]), 6000);
  }, [focusCreatedItems, itineraryByDays]);

  // Keep every day open unless the viewer collapsed it.
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(() => {
    if (itineraryByDays.length === 0) return;

    const initialExpanded = {};
    let hasChanges = false;
    itineraryByDays.forEach((day) => {
      if (expandedDays[day.dayNumber] === undefined) {
        initialExpanded[day.dayNumber] = true;
        hasChanges = true;
      }
    });
    if (hasChanges) {
      setExpandedDays((prev) => ({ ...prev, ...initialExpanded }));
    }
  }, [itineraryByDays]);

  // Handler for expand/collapse all
  const handleExpandCollapseAll = (event, value) => {
    if (!value) return;
    setExpandCollapseMode(value);
    if (value === 'expand') {
      const newExpanded = {};
      itineraryByDays.forEach(day => {
        // Always expand all days when "Expand All Planned" is clicked
        newExpanded[day.dayNumber] = true;
      });
      setExpandedDays(newExpanded);
    } else if (value === 'collapse') {
      const newExpanded = {};
      itineraryByDays.forEach(day => {
        newExpanded[day.dayNumber] = false;
      });
      setExpandedDays(newExpanded);
    }
  };

  const handleOpenDetailModal = (item) => {
    // A traveler's road trip shown on the group trip is edited on the traveler's own trip.
    setRoadTripMapTripId(item.traveler_trip_id || null);
    setRoadTripMapReadOnly(isReadOnlyGroupPlanItem(item, { isPartner }));
    if (item._isRoadTripCard && item._roadTripData) {
      setRoadTripMapData(item._roadTripData);
      setRoadTripMapItemName(item._roadTripTripTitle || item._roadTripTitle || item.name);
      setRoadTripMapOpen(true);
      return;
    }
    const transportData = item.transportation_data;
    const rtd = transportData?.road_trip_data;
    if (rtd && rtd.orderedStops && rtd.orderedStops.length >= 2) {
      setRoadTripMapData(rtd);
      setRoadTripMapItemName(item.name);
      setRoadTripMapOpen(true);
    }
  };

  const handleOpenRoadTripPlanner = (selectedDay = null) => {
    if (!canMutateItinerary) return;
    setRoadTripAnchorDate(selectedDay);
    setRoadTripPlannerOpen(true);
  };

  const handleCloseRoadTripPlanner = () => {
    setRoadTripPlannerOpen(false);
    setRoadTripAnchorDate(null);
  };

  const handleRoadTripSaved = () => {
    handleCloseRoadTripPlanner();
    fetchItinerary();
    if (onItineraryChange) {
      onItineraryChange();
    }
  };

  const renderRoadTripAction = (day) => (
    canEditMasterItinerary && (
      <Button
        sx={documentTextButtonSx}
        onClick={() => handleOpenRoadTripPlanner(day.date)}
      >
        {t('itinerary.planRoadTrip')}
      </Button>
    )
  );

  const handleOpenDialog = (item = null, selectedDay = null) => {
    if (!canMutateItinerary) return;
    if (item) {
      // A multi-leg flight can be stored as several separate itinerary items
      // (one per leg). When editing any of them, load the full journey so the
      // modal shows every leg and saves update all of the underlying items.
      const journeyItem = buildFlightJourneyEditItem(item, itinerary);
      const editTarget = journeyItem || item;
      setEditingItem(editTarget);
      item = editTarget;
      
      
      // Convert MySQL DATETIME to local time format string without timezone conversion
      const formatMySQLDateTime = (mysqlDateTime) => {
        if (!mysqlDateTime) return null;
        
        // MySQL returns DATETIME as string like "2025-12-01 14:00:00" (no timezone info)
        // Parse it directly without timezone conversion
        const dateStr = mysqlDateTime.toString();
        
        // Handle different formats: "2025-12-01 14:00:00" or "2025-12-01T14:00:00.000Z"
        let year, month, day, hours, minutes;
        
        if (dateStr.includes('T')) {
          // ISO format: "2025-12-01T14:00:00.000Z"
          const [datePart, timePart] = dateStr.split('T');
          const [y, m, d] = datePart.split('-');
          const [h, min] = timePart.split(':');
          year = y;
          month = m;
          day = d;
          hours = h;
          minutes = min;
        } else {
          // MySQL format: "2025-12-01 14:00:00"
          const [datePart, timePart] = dateStr.split(' ');
          const [y, m, d] = datePart.split('-');
          const [h, min] = timePart.split(':');
          year = y;
          month = m;
          day = d;
          hours = h;
          minutes = min;
        }
        
        const formatted = `${year}-${month}-${day}T${hours}:${minutes}`;
        console.log(`  Converted ${mysqlDateTime} -> ${formatted} (no timezone conversion)`);
        return formatted;
      };
      
      const startDateTime = formatMySQLDateTime(item.start_datetime);
      const endDateTime = formatMySQLDateTime(item.end_datetime);
      
      const newFormData = {
        name: item.name,
        location: item.location,
        google_map_link: item.google_map_link || '',
        category: item.category,
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        description: item.description || '',
        image_url: item.image_url || '',
        cost: item.cost || '',
        cost_currency: item.cost_currency || 'USD', // Added missing field
        transportation_type: item.transportation_type || '',
        transportation_data: item.transportation_data || {}
      };
      
      setFormData(newFormData);
    } else {
      setEditingItem(null);
      
      // Set default dates for new activity - single day by default
      let defaultStartDate = '';
      let defaultEndDate = '';
      
      if (selectedDay) {
        // If a specific day is selected, use that day with default times
        const year = selectedDay.getFullYear();
        const month = String(selectedDay.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDay.getDate()).padStart(2, '0');
        
        // Default: 9:00 AM - 5:00 PM on the same day
        defaultStartDate = `${year}-${month}-${day}T09:00`;
        defaultEndDate = `${year}-${month}-${day}T17:00`;
        
        console.log('=== NEW ACTIVITY - Selected Day ===');
        console.log('Selected day:', selectedDay);
        console.log('Formatted start:', defaultStartDate);
        console.log('Formatted end:', defaultEndDate);
      } else if (trip?.start_date) {
        // Default to first day of trip with default times
        // Parse the date string to avoid timezone issues
        const [year, month, day] = trip.start_date.split('-');
        
        // Default: 9:00 AM - 5:00 PM on the same day
        defaultStartDate = `${year}-${month}-${day}T09:00`;
        defaultEndDate = `${year}-${month}-${day}T17:00`;
        
        console.log('=== NEW ACTIVITY - Trip Start Day ===');
        console.log('Trip start date:', trip.start_date);
        console.log('Formatted start:', defaultStartDate);
        console.log('Formatted end:', defaultEndDate);
      }
      
      setFormData({
        name: '',
        location: '',
        google_map_link: '',
        category: 'other',
        start_datetime: defaultStartDate,
        end_datetime: defaultEndDate,
        description: '',
        image_url: '',
        cost: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setSubmitting(false);
    setActionError(null);
    setFormData({
      name: '',
      location: '',
      google_map_link: '',
      category: 'other',
      start_datetime: '',
      end_datetime: '',
      description: '',
      image_url: '',
      cost: ''
    });
  };

  const handleImportComplete = (summary) => {
    console.log('📅 ICS Import: handleImportComplete called, refreshing itinerary...');
    // Refresh the itinerary data
    fetchItinerary();
  };

  const handleDownloadSample = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/sample-itinerary-csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sample-itinerary-import.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      alert('Failed to download sample CSV');
    }
  };

  const handleSubmit = async (submittedFormData) => {
    setSubmitting(true);
    setActionError(null);
    try {
      // Validation is now handled in ActivityModal component

      if (editingItem) {
        // Filter form data to only include fields expected by the main itinerary API
        const itineraryData = {
          name: submittedFormData.name,
          location: submittedFormData.location,
          google_map_link: submittedFormData.google_map_link,
          latitude: submittedFormData.latitude ?? null,
          longitude: submittedFormData.longitude ?? null,
          place_id: submittedFormData.place_id ?? null,
          geo_source: submittedFormData.geo_source ?? null,
          category: submittedFormData.category,
          start_datetime: submittedFormData.start_datetime,
          end_datetime: submittedFormData.end_datetime,
          description: submittedFormData.description,
          image_url: submittedFormData.image_url || null,
          cost: submittedFormData.cost,
          cost_currency: submittedFormData.cost_currency,
          transportation_type: submittedFormData.transportation_type,
          transportation_data: submittedFormData.transportation_data
        };
        console.log('Filtered itinerary data for update:', itineraryData);

        if (itineraryData.transportation_type === 'flight' && itineraryData.transportation_data?.legs?.length) {
          itineraryData.transportation_data = {
            ...itineraryData.transportation_data,
            legs: await lookupFlightLegs(itineraryData.transportation_data.legs),
          };
        }

        const journeyItems = resolveFlightJourneyItems(editingItem, itinerary);
        const legs = itineraryData.transportation_data?.legs || [];
        // Only split across itinerary rows when the journey already spans
        // multiple items. A single multi-leg row updates in place.
        const isGroupedFlightUpdate =
          itineraryData.transportation_type === 'flight'
          && (journeyItems.length > 1 || Boolean(parseCompositeItineraryId(editingItem?.id)));

        if (isGroupedFlightUpdate && legs.length > 0) {
          const updateCount = Math.max(journeyItems.length, legs.length);
          for (let index = 0; index < updateCount; index += 1) {
            const existingItem = journeyItems[index] || { id: undefined };
            const leg = legs[index];
            if (!leg) {
              if (existingItem.id != null) {
                await deleteItineraryItem(existingItem.id);
              }
              continue;
            }
            const payload = buildFlightLegUpdatePayload(itineraryData, leg, existingItem, {
              isFirstLeg: index === 0,
            });
            if (existingItem.id != null) {
              await updateItineraryItem(existingItem.id, payload);
            } else {
              await addItineraryItem(payload);
            }
          }
        } else {
          const itemId = journeyItems[0]?.id ?? editingItem.id;
          if (parseCompositeItineraryId(itemId)) {
            throw new Error('Unable to update grouped flight. Please refresh and try again.');
          }
          await updateItineraryItem(itemId, itineraryData);
        }
      } else {
        // Filter form data to only include fields expected by the main itinerary API
        const itineraryData = {
          name: submittedFormData.name,
          location: submittedFormData.location,
          google_map_link: submittedFormData.google_map_link,
          latitude: submittedFormData.latitude ?? null,
          longitude: submittedFormData.longitude ?? null,
          place_id: submittedFormData.place_id ?? null,
          geo_source: submittedFormData.geo_source ?? null,
          category: submittedFormData.category,
          start_datetime: submittedFormData.start_datetime,
          end_datetime: submittedFormData.end_datetime,
          description: submittedFormData.description,
          image_url: submittedFormData.image_url || null,
          cost: submittedFormData.cost,
          cost_currency: submittedFormData.cost_currency,
          transportation_type: submittedFormData.transportation_type,
          transportation_data: submittedFormData.transportation_data
        };
        if (itineraryData.transportation_type === 'flight' && itineraryData.transportation_data?.legs?.length) {
          itineraryData.transportation_data = {
            ...itineraryData.transportation_data,
            legs: await lookupFlightLegs(itineraryData.transportation_data.legs),
          };
        }
        await addItineraryItem(itineraryData);
      }

      setActionError(null);
      handleCloseDialog();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || t('itinerary.saveFailed');
      setActionError(errorMessage);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (itemId) => {
    const compositeIds = parseCompositeItineraryId(itemId);
    setItemToDelete(compositeIds?.length ? compositeIds : itemId);
    setDeleteDialogOpen(true);
  };

  // Delete all itinerary items belonging to a road trip group
  const handleDeleteRoadTripGroup = (groupKey) => {
    // Find all item IDs that share this road trip group key
    const idsToDelete = itinerary
      .filter(it => {
        const itRtd = it.transportation_data?.road_trip_data;
        if (!itRtd) return false;
        const itKey = itRtd.groupId || itRtd.encodedPolyline
          || (itRtd.orderedStops ? itRtd.orderedStops.map(s => s.name).join('|') : null);
        return itKey === groupKey;
      })
      .map(it => it.id);
    if (idsToDelete.length > 0) {
      setItemToDelete(idsToDelete);
      setDeleteDialogOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        // Support deleting a single ID or an array of IDs (road trip group)
        const ids = Array.isArray(itemToDelete) ? itemToDelete : [itemToDelete];
        for (const id of ids) {
          await deleteItineraryItem(id);
        }
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      } catch (err) {
        console.error('Error deleting itinerary item:', err);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setActionError(err.response?.data?.error || t('itinerary.saveFailed'));
        fetchItinerary();
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleAddNote = async (itemId) => {
    if (!newNote.trim()) return;

    try {
      await addNote(itemId, newNote.trim());
      setNewNote('');
      setNoteDialogs(prev => ({ ...prev, [itemId]: false }));
      fetchItinerary(); // Refresh itinerary to show the new note
    } catch (err) {
      console.error('Error adding note:', err);
    }
  };


  const canEditItem = (item) => {
    if (!canEditMasterItinerary) return false;
    if (isReadOnlyGroupPlanItem(item, { isPartner })) return false;
    // Creator and partner can always edit
    if (isCreator || isPartner) return true;
    
    // User who added the item can edit (API uses created_by; some clients send added_by)
    if (item.added_by === user?.id || item.created_by === user?.id) return true;
    
    // For subgroup items, any participant can edit
    if (item.visibility === 'subgroup' && item.participants) {
      const participantIds = item.participants.map(p => p.user_id || p.id);
      if (participantIds.includes(user?.id)) return true;
    }
    
    return false;
  };

  const toggleDayExpansion = (dayNumber) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dayNumber]: prev[dayNumber] === false,
    }));
  };

  const handleItineraryReorder = async (updates) => {
    setIsReorderingItinerary(true);
    try {
      await Promise.all(
        updates.map(({ id, start_datetime, end_datetime, transportation_data }) => {
          const payload = {};
          if (start_datetime !== undefined) {
            payload.start_datetime = start_datetime;
          }
          if (end_datetime !== undefined) {
            payload.end_datetime = end_datetime;
          }
          if (transportation_data !== undefined) {
            payload.transportation_data = transportation_data;
          }
          return updateItineraryItem(id, payload);
        })
      );
      if (onItineraryChange) {
        onItineraryChange();
      }
      await fetchItinerary();
    } catch (err) {
      console.error('Failed to reorder itinerary:', err);
      await fetchItinerary();
    } finally {
      setIsReorderingItinerary(false);
    }
  };

  const formatDurationMinutes = (seconds) => {
    const total = Math.max(0, Math.round(Number(seconds || 0) / 60));
    if (total < 60) return t('itinerary.optimizeMinutes', { count: total });
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    if (!mins) return t('itinerary.optimizeHours', { count: hours });
    return t('itinerary.optimizeHoursMinutes', { hours, minutes: mins });
  };

  const handleOptimizeDay = async (day, event) => {
    event?.stopPropagation?.();
    if (!canEditMasterItinerary) return;
    const dateKey = getDayDateKey(day.date);
    setOptimizeDayKey(dateKey);
    setOptimizeDayOpen(true);
    setOptimizeDayLoading(true);
    setOptimizeDayError('');
    setOptimizeDayProposal(null);
    try {
      const res = await api.post(`/trips/${tripId}/itinerary/optimize-day`, { date: dateKey });
      setOptimizeDayProposal(res.data);
    } catch (err) {
      setOptimizeDayError(err.response?.data?.error || t('itinerary.optimizeFailed'));
    } finally {
      setOptimizeDayLoading(false);
    }
  };

  const handleApplyOptimizeDay = async () => {
    if (!optimizeDayProposal?.changed || !optimizeDayKey) return;
    const day = itineraryByDays.find((entry) => getDayDateKey(entry.date) === optimizeDayKey);
    if (!day) return;

    const byId = new Map(day.items.map((item) => [item.id, item]));
    const ordered = (optimizeDayProposal.proposedOrder || [])
      .map((id) => byId.get(id))
      .filter(Boolean);
    if (ordered.length !== day.items.length) {
      setOptimizeDayError(t('itinerary.optimizeStale'));
      return;
    }

    const updates = recalculateDayItemTimes(ordered, optimizeDayKey).filter((update) => {
      const original = byId.get(update.id);
      if (!original) return false;
      return original.start_datetime !== update.start_datetime
        || (original.end_datetime || null) !== (update.end_datetime || null);
    });

    setOptimizeDayApplying(true);
    setOptimizeDayError('');
    try {
      await handleItineraryReorder(updates);
      setOptimizeDayOpen(false);
      setOptimizeDayProposal(null);
    } catch (err) {
      setOptimizeDayError(err.response?.data?.error || t('itinerary.optimizeApplyFailed'));
    } finally {
      setOptimizeDayApplying(false);
    }
  };

  const handleOpenMaps = (item) => {
    const url = item?.google_map_link
      || (item?.location ? generateGoogleMapsLink(item.location) : null);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleStop = (itemId) => {
    setExpandedStopIds((prev) => {
      const willExpand = !prev[itemId];
      if (willExpand && onItemExpand) {
        onItemExpand(itemId);
      }
      return { ...prev, [itemId]: willExpand };
    });
  };

  // Reservation cards expand in place; the title opens a read-only detail modal.
  const handleReservationSelect = (itemId) => {
    const item = itinerary.find((entry) => String(entry.id) === String(itemId));
    if (!item) return;
    const detailTarget = buildFlightJourneyEditItem(item, itinerary) || item;
    setDetailItem(detailTarget);
    setDetailModalOpen(true);
  };

  const handleReservationEdit = (itemId) => {
    const item = itinerary.find((entry) => String(entry.id) === String(itemId));
    if (!item) return;
    const editTarget = buildFlightJourneyEditItem(item, itinerary) || item;
    if (!canEditItem(editTarget) && !canEditItem(item)) return;
    setActionError(null);
    handleOpenDialog(editTarget);
  };

  // A flight journey spans one item per leg; removing the card removes every leg
  const handleReservationRemove = (itemId) => {
    const item = itinerary.find((entry) => String(entry.id) === String(itemId));
    if (!item) return;
    const target = buildFlightJourneyEditItem(item, itinerary) || item;
    if (!canEditItem(target) && !canEditItem(item)) return;
    handleDelete(target.id);
  };

  const handleDeleteFromDetailModal = () => {
    if (detailItem) handleDelete(detailItem.id);
  };

  const canMoveItemToPersonal = (item) => {
    if (!item?.id || !canEditMasterItinerary) return false;
    // Group-synced plan rows stay organizer-managed; everything else can move.
    if (isReadOnlyGroupPlanItem(item, { isPartner })) return false;
    return Boolean(isCreator || isPartner || user?.id);
  };

  const openMoveToPersonal = async (item) => {
    if (!canMoveItemToPersonal(item)) return;
    setMovePersonalItem(item);
    setMovePersonalOpen(true);
    setMovePersonalBusy(true);
    try {
      const authOpts = isPartner ? { usePartnerAuth: true } : {};
      const res = await api.get(`/member-itinerary/trip/${tripId}/members`, authOpts);
      const members = res.data?.members || [];
      setMovePersonalMembers(members);
      const defaultId = isPartner || isCreator
        ? (members[0]?.user_id || user?.id || '')
        : (user?.id || '');
      setMovePersonalUserId(defaultId || '');
    } catch (err) {
      console.error('Failed to load members for move:', err);
      setMovePersonalMembers(user?.id ? [{ user_id: user.id, username: user.username || user.email }] : []);
      setMovePersonalUserId(user?.id || '');
    } finally {
      setMovePersonalBusy(false);
    }
  };

  const confirmMoveToPersonal = async () => {
    if (!movePersonalItem?.id || !movePersonalUserId) return;
    setMovePersonalBusy(true);
    setActionError(null);
    try {
      const authOpts = isPartner ? { usePartnerAuth: true } : {};
      await api.post(
        `/trips/${tripId}/itinerary/${movePersonalItem.id}/move-to-personal`,
        { userId: movePersonalUserId },
        authOpts
      );
      setMovePersonalOpen(false);
      setMovePersonalItem(null);
      await fetchItinerary();
    } catch (err) {
      setActionError(err.response?.data?.error || t('itinerary.moveFailed'));
    } finally {
      setMovePersonalBusy(false);
    }
  };

  const handleEditFromDetailModal = () => {
    setDetailModalOpen(false);
    if (detailItem) {
      setActionError(null);
      handleOpenDialog(detailItem);
    }
  };

  const handleStopDelete = (item) => {
    if (item._isRoadTripCard) {
      handleDeleteRoadTripGroup(item._roadTripGroupId);
      return;
    }
    handleDelete(item.id);
  };

  const renderDayAddActivitySection = (day) => (
    canEditMasterItinerary && (
      <Box sx={{ display: 'flex', gap: 2.5, pt: 1.5, pb: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button sx={documentTextButtonSx} onClick={() => handleOpenDialog(null, day.date)}>
          {t('itinerary.addActivity')}
        </Button>
        {renderRoadTripAction(day)}
      </Box>
    )
  );

  const renderDayEmptySection = (day) => (
    canEditMasterItinerary ? (
      <Box sx={{ display: 'flex', gap: 2.5, py: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button sx={documentTextButtonSx} onClick={() => handleOpenDialog(null, day.date)}>
          {t('itinerary.planYourFirstActivity')}
        </Button>
        {renderRoadTripAction(day)}
      </Box>
    ) : null
  );

  const itemPinNumbers = useMemo(() => {
    const pins = {};
    let pin = 1;
    itineraryByDays.forEach((day) => {
      day.items.forEach((item) => {
        if (item.id != null && pins[item.id] == null) {
          pins[item.id] = pin;
          pin += 1;
        }
      });
    });
    return pins;
  }, [itineraryByDays]);

  const handleChangeItemPhoto = async (url) => {
    if (!photoItem) return;
    const journeyItems = resolveFlightJourneyItems(photoItem, itinerary);
    const itemId = journeyItems[0]?.id ?? photoItem.id;
    if (itemId == null || parseCompositeItineraryId(itemId)) return;
    await updateItineraryItem(itemId, { image_url: url || null });
    setPhotoItem((current) => (current ? { ...current, image_url: url || '' } : current));
  };

  const renderSpineStop = (item, isLast = false, currentDayDate = null, nextItem = null) => (
    <ItineraryStop
      item={item}
      isLast={isLast}
      nextItem={nextItem}
      currentDayDate={currentDayDate}
      pin={itemPinNumbers[item.id]}
      expanded={Boolean(expandedStopIds[item.id])}
      onToggle={() => toggleStop(item.id)}
      onEdit={handleOpenDialog}
      onDelete={handleStopDelete}
      onMoveToPersonal={openMoveToPersonal}
      onOpenMaps={handleOpenMaps}
      onOpenRoute={handleOpenDetailModal}
      onAddNote={(stop) => setNoteDialogs((prev) => ({ ...prev, [stop.id]: true }))}
      onChangePhoto={() => setPhotoItem(item)}
      canEdit={canEditItem(item)}
      canMoveToPersonal={canMoveItemToPersonal(item)}
      showGroupPlanTag={Boolean(trip?.master_trip_id) && isReadOnlyGroupPlanItem(item, { isPartner })}
      highlighted={highlightStopIds.includes(item.id)}
      onToggleVisibility={canManageDrafts ? setItemVisibility : undefined}
    />
  );

  const renderItineraryItem = (item, isLast = false, currentDayDate = null) => {
    const day = itineraryByDays.find((entry) => (
      currentDayDate && startOfDay(entry.date).getTime() === startOfDay(currentDayDate).getTime()
    ));
    const index = day?.items?.findIndex((entry) => entry.id === item.id) ?? -1;
    const nextItem = index >= 0 && day ? day.items[index + 1] : null;
    return renderSpineStop(item, isLast, currentDayDate, nextItem);
  };

  const renderRoadTripCard = (item) => renderSpineStop(item, false, null, null);

  const renderDaySection = (day) => {
    const hasItems = day.items.length > 0;
    const isExpanded = expandedDays[day.dayNumber] !== false;
    const isOpen = hasItems && isExpanded;
    const dayLabel = format(day.date, 'EEEE, d MMMM', { locale: dateLocale });
    const timeSpan = getDayTimeSpan(day.items, formatTime24);
    const dayInTransit = dayHasTransit(day.date, itinerary);
    const dayCardSx = {
      mt: 1.5,
      bgcolor: isOpen ? DOCUMENT.surface : DOCUMENT.canvas,
      border: isOpen ? `1px solid ${DOCUMENT.ruleHard}` : `1px dashed ${DOCUMENT.ruleHard}`,
    };

    if (!hasItems) {
      return (
        <Box
          key={day.dayNumber}
          data-testid={`itinerary-empty-day-${day.dayNumber}`}
          sx={dayCardSx}
        >
          <ItineraryDayDragArea
            day={day}
            renderItem={renderItineraryItem}
            renderRoadTripCard={renderRoadTripCard}
            renderAddActivitySection={() => null}
            renderEmptyDaySection={() => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2.25,
                  px: 2.25,
                  py: 1.75,
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', color: DOCUMENT.ink3, width: 54, flexShrink: 0 }}>
                  {t('itinerary.dayNumber', { number: String(day.dayNumber).padStart(2, '0') })}
                </Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 16, sm: 18 }, fontWeight: 400, color: DOCUMENT.ink }}>
                  {dayLabel}
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3 }}>
                    {t(getEmptyDayStatusKey(day.date, itinerary))}
                  </Typography>
                  {canEditMasterItinerary && (
                    <Button
                      sx={documentTextButtonSx}
                      onClick={() => handleOpenDialog(null, day.date)}
                    >
                      {t('itinerary.addActivity')}
                    </Button>
                  )}
                  {renderRoadTripAction(day)}
                </Box>
              </Box>
            )}
          />
        </Box>
      );
    }

    return (
      <Box
        key={day.dayNumber}
        data-testid={`itinerary-day-${day.dayNumber}`}
        sx={dayCardSx}
      >
        <Box
          onClick={() => toggleDayExpansion(day.dayNumber)}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1.5,
            px: 1.75,
            pt: 1.25,
            pb: 1,
            borderBottom: `1px solid ${DOCUMENT.rule}`,
            cursor: 'pointer',
          }}
        >
          <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', color: DOCUMENT.accent, width: 48, flexShrink: 0 }}>
            {t('itinerary.dayNumber', { number: String(day.dayNumber).padStart(2, '0') })}
          </Typography>
          <Typography
            component="h3"
            sx={{ fontFamily: DOCUMENT.sans, fontSize: { xs: 13, sm: 14 }, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.3, color: DOCUMENT.ink }}
          >
            {dayLabel}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Typography
              data-testid={`itinerary-day-count-${day.dayNumber}`}
              sx={{ fontSize: 11.5, color: DOCUMENT.ink3 }}
            >
              {t('itinerary.activity', { count: day.items.length })}
            </Typography>
            {dayInTransit && (
              <Typography sx={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.19em', textTransform: 'uppercase', color: DOCUMENT.accent }}>
                {t('itinerary.status.inTransit')}
              </Typography>
            )}
            {timeSpan && (
              <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, fontVariantNumeric: 'tabular-nums' }}>
                {timeSpan}
              </Typography>
            )}
            {canEditMasterItinerary && day.items.length >= 3 && (
              <Tooltip title={t('itinerary.optimizeDayHint')}>
                <Button
                  size="small"
                  startIcon={<RouteIcon sx={{ fontSize: 16 }} />}
                  sx={{ ...documentTextButtonSx, fontSize: 11.5 }}
                  onClick={(event) => handleOptimizeDay(day, event)}
                  data-testid={`optimize-day-${day.dayNumber}`}
                >
                  {t('itinerary.optimizeDay')}
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Collapse in={isExpanded} timeout={320}>
          <Box
            sx={{
              position: 'relative',
              px: 2.25,
              pb: 1.5,
              '& [data-testid^="itinerary-stop-"]': {
                borderTop: `1px solid ${DOCUMENT.rule}`,
              },
              '& [data-testid^="itinerary-stop-"]:first-of-type': {
                borderTop: 0,
              },
            }}
          >
            <ItineraryDayDragArea
              day={day}
              renderItem={renderItineraryItem}
              renderRoadTripCard={renderRoadTripCard}
              renderAddActivitySection={renderDayAddActivitySection}
              renderEmptyDaySection={renderDayEmptySection}
            />
          </Box>
        </Collapse>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: { xs: 1, md: 1 }, px: { xs: 0, md: 1 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Box sx={{
        p: { xs: 0, md: 0.5 },
        background: 'transparent',
        boxShadow: 'none',
      }}>
        <Box sx={{ mb: { xs: 2, md: 3.5 }, display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="scrollable"
            scrollButtons={false}
            allowScrollButtonsMobile
            textColor="inherit"
            TabIndicatorProps={{ sx: documentTabIndicatorSx }}
            sx={{ ...documentSubTabsSx, flex: 1, minWidth: 0 }}
          >
            <Tab value="itinerary" label={t('itinerary.tabs.itinerary')} />
            {showPackageAddOns && (
              <Tab value="addons" label={t('itinerary.tabs.addons')} />
            )}
            <Tab
              value="myItinerary"
              label={isPartner ? t('itinerary.tabs.clientItinerary') : t('itinerary.tabs.personalItinerary')}
            />
            {showSuggestions && (
              <Tab value="suggestions" label={t('itinerary.tabs.suggestions')} />
            )}
            <Tab value="topRated" label={t('itinerary.tabs.topRated')} />
          </Tabs>
          {isCreator && (
            <Tooltip title={t('publishGuide.tooltip')} arrow placement="left">
              <Button
                onClick={() => setPublishGuideOpen(true)}
                size="small"
                sx={{
                  ...documentTextButtonSx,
                  mb: 1.5,
                  flexShrink: 0,
                  display: { xs: 'none', md: 'inline-flex' },
                }}
              >
                {t('publishGuide.publish')}
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Controls Row: Add Activity + Expand/Collapse All (move inside the Itinerary tab panel) */}
        {currentTab === 'itinerary' && (
          <>
            {canMutateItinerary && canManageDrafts && itinerary.some((item) => item.client_visible === 0 || item.client_visible === false) && (
              <Box
                data-testid="itinerary-drafts-banner"
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: DOCUMENT.canvas,
                  border: `1px solid ${DOCUMENT.ruleHard}`,
                }}
              >
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, flex: '1 1 220px' }}>
                  {t('itinerary.draftsBanner', {
                    count: itinerary.filter((item) => item.client_visible === 0 || item.client_visible === false).length
                  })}
                </Typography>
                <Button
                  data-testid="itinerary-publish-all"
                  sx={documentTextButtonSx}
                  onClick={() => publishAllDrafts()}
                >
                  {t('itinerary.publishAllDrafts')}
                </Button>
              </Box>
            )}
            <Box
              data-testid="itinerary-map-row"
              sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
            >
              {canEditMasterItinerary ? (
                <Box data-testid="itinerary-import-row" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    onClick={() => setImportDialogOpen(true)}
                    size="small"
                    aria-label={t('itinerary.importCSV')}
                    sx={documentOutlineIconButtonSx}
                  >
                    <TableChartIcon sx={{ fontSize: 18 }} />
                  </Button>
                  <Button
                    onClick={() => setIcsImportDialogOpen(true)}
                    size="small"
                    aria-label={t('itinerary.importICS')}
                    sx={documentOutlineIconButtonSx}
                  >
                    <CalendarTodayIcon sx={{ fontSize: 18 }} />
                  </Button>
                  <Tooltip title={t('emailImport.title', 'Import Reservations from Email')}>
                    <Button
                      onClick={() => setEmailImportOpen(true)}
                      size="small"
                      aria-label={t('emailImport.title', 'Import Reservations from Email')}
                      sx={documentOutlineIconButtonSx}
                    >
                      <ForwardToInboxIcon sx={{ fontSize: 18 }} />
                    </Button>
                  </Tooltip>
                  <Button
                    variant="text"
                    onClick={handleDownloadSample}
                    size="small"
                    sx={documentTextButtonSx}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DownloadIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('itinerary.csvSample')}</Box>
                    </Box>
                  </Button>
                </Box>
              ) : <Box />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setMapDrawerMode('view');
                    setMapDrawerOpen(true);
                  }}
                  sx={documentOutlineButtonSx}
                  data-testid="itinerary-open-map"
                  startIcon={<MapIcon sx={{ fontSize: 18 }} />}
                >
                  {t('tripMap.openMap')}
                </Button>
                {canEditMasterItinerary ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setMapDrawerMode('plan');
                      setMapDrawerOpen(true);
                    }}
                    sx={documentTextButtonSx}
                    data-testid="itinerary-plan-on-map"
                  >
                    {t('tripMap.planOnMap')}
                  </Button>
                ) : null}
              </Box>
            </Box>
            {canEditMasterItinerary && (
              <Box sx={{
                mb: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'flex-end',
                gap: 1,
              }}>
                {onSuggestWithAi ? (
                  <Button
                    data-testid="trip-suggest-with-ai"
                    onClick={onSuggestWithAi}
                    sx={{ ...documentOutlineButtonSx, width: { xs: '100%', sm: 'auto' } }}
                  >
                    <PaidFeatureLabel show={showPaidAiBadge(trip, { entitlements: partnerEntitlements })}>
                      {t('partnerManage.suggestWithAi')}
                    </PaidFeatureLabel>
                  </Button>
                ) : null}
                <Button
                  data-testid="itinerary-add-activity"
                  onClick={() => handleOpenDialog()}
                  sx={{ ...documentOutlineButtonSx, width: { xs: '100%', sm: 'auto' } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('itinerary.addActivity')}</Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>{t('itinerary.add')}</Box>
                  </Box>
                </Button>
              </Box>
            )}
            {itinerary.length === 0 && (
              <Box sx={{ py: 3, mb: 2, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 21, fontWeight: 400, mb: 0.75, color: DOCUMENT.ink }}>
                  {t('itinerary.emptyTitle')}
                </Typography>
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2.5, maxWidth: 520, lineHeight: 1.6 }}>
                  {t(canMutateItinerary ? 'itinerary.emptyMessage' : 'itinerary.completedReadOnly')}
                </Typography>
                {canMutateItinerary && (onNavigateToExplore || (!onSuggestWithAi && onNavigateToAIConcierge)) ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  {!onSuggestWithAi && onNavigateToAIConcierge ? (
                    <Button
                      sx={documentOutlineButtonSx}
                      onClick={() => {
                        if (isMobileApp()) {
                          openAIConciergeinBrowser();
                        } else {
                          onNavigateToAIConcierge();
                        }
                      }}
                    >
                      <PaidFeatureLabel show>
                        {t('itinerary.aiConcierge')}
                      </PaidFeatureLabel>
                    </Button>
                  ) : null}
                  {onNavigateToExplore ? (
                    <Button
                      sx={documentOutlineButtonSx}
                      onClick={() => onNavigateToExplore()}
                    >
                      {t('itinerary.exploreDiscover')}
                    </Button>
                  ) : null}
                </Box>
                ) : null}
              </Box>
            )}
            {/* Multi-day items summary */}
            <ItineraryReservations
              items={displayItinerary}
              onSelectItem={handleReservationSelect}
              onEditItem={handleReservationEdit}
              onRemoveItem={handleReservationRemove}
              canEditItem={(itemId) => {
                const item = itinerary.find((entry) => String(entry.id) === String(itemId));
                if (!item) return false;
                const journeyItem = buildFlightJourneyEditItem(item, itinerary) || item;
                return canEditItem(journeyItem) || canEditItem(item);
              }}
              flightStatusEnabled={isTripEligibleForFlightStatus(trip)}
            />

            <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 2 } }}>
              <DocumentSectionHead
                sx={{ alignItems: 'center' }}
                title={t('itinerary.dayByDay')}
                meta={t('itinerary.dayByDayMeta', {
                  days: itineraryByDays.length,
                  planned: plannedStopCount,
                })}
                actions={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ToggleButtonGroup
                      value={expandCollapseMode}
                      exclusive
                      onChange={handleExpandCollapseAll}
                      size="small"
                      sx={documentToggleSx}
                    >
                      <ToggleButton value="expand">
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      </ToggleButton>
                      <ToggleButton value="collapse">
                        <ExpandLessIcon sx={{ fontSize: 18 }} />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                }
              />
              <ItineraryDragDropProvider
                days={itineraryByDays}
                disabled={!canEditMasterItinerary}
                isSaving={isReorderingItinerary}
                renderItem={renderItineraryItem}
                renderRoadTripCard={renderRoadTripCard}
                tripStartDate={trip?.start_date?.split('T')[0] || trip?.start_date}
                canDragItem={canDragItineraryItem}
                onReorder={handleItineraryReorder}
              >
                {itineraryByDays.map(renderDaySection)}
              </ItineraryDragDropProvider>
            </Box>

            <Suspense fallback={null}>
              <TripMapDrawer
                open={mapDrawerOpen}
                onClose={() => setMapDrawerOpen(false)}
                tripId={tripId}
                trip={trip}
                usePartnerAuth={Boolean(isPartner)}
                canPlan={Boolean(canEditMasterItinerary)}
                initialMode={mapDrawerMode}
                onAdded={() => {
                  fetchItinerary();
                  onItineraryChange?.();
                }}
              />
            </Suspense>
          </>
        )}

        {/* My Itinerary Tab */}
        {showPackageAddOns && currentTab === 'addons' && (
          <TripPackageAddOns
            tripId={tripId}
            onPurchased={() => {
              fetchItinerary();
              if (onItineraryChange) onItineraryChange();
            }}
          />
        )}

        <Box sx={{ display: currentTab === 'myItinerary' ? 'block' : 'none' }}>
          <MyItinerary 
            trip={trip}
            isCreator={isCreator}
            isPartner={isPartner}
            onAddActivity={canMutateItinerary ? (activityData) => {
              // Open the add activity dialog with pre-filled data
              setFormData({
                name: activityData.name || '',
                location: activityData.location || '',
                google_map_link: activityData.google_map_link || '',
                category: activityData.category || 'other',
                start_datetime: '',
                end_datetime: '',
                description: activityData.description || '',
                image_url: activityData.image_url || '',
                cost: ''
              });
              setDialogOpen(true);
              setEditingItem(null);
            } : undefined}
            onCreateSuggestion={(activityData) => {
              // Open suggestion creation dialog with pre-filled data
              if (openSuggestionDialog) {
                openSuggestionDialog(activityData);
                // Switch to Suggestions tab to show the newly created suggestion
                setCurrentTab('suggestions');
              }
            }}
          />
        </Box>

        {/* TripSuggestions component - only for non-partners */}
        {showSuggestions && (
          <Box sx={{ display: currentTab === 'suggestions' ? 'block' : 'none' }}>
            <Box sx={{ width: '100%' }}>
              <TripSuggestions 
                tripId={trip?.id}
                trip={trip}
                isCreator={isCreator}
                isPartner={isPartner}
                onItineraryUpdate={fetchItinerary}
                onSwitchToItinerary={() => setCurrentTab('itinerary')}
                onOpenSuggestionDialog={(dialogFunction) => {
                  setOpenSuggestionDialog(() => dialogFunction);
                }}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ width: '100%', display: currentTab === 'topRated' ? 'block' : 'none' }}>
          <TopRatedSection 
            trip={trip}
            isCreator={isCreator}
            isPartner={isPartner}
            isActive={currentTab === 'topRated'}
            onAddActivity={(activityData) => {
              // Open the add activity dialog with pre-filled data
              setFormData({
                name: activityData.name || '',
                location: activityData.location || '',
                google_map_link: activityData.google_map_link || '',
                category: activityData.category || 'other',
                start_datetime: '',
                end_datetime: '',
                description: activityData.description || '',
                image_url: activityData.image_url || '',
                cost: ''
              });
              setEditingItem(null);
              setDialogOpen(true);
            }}
            onCreateSuggestion={(activityData) => {
              // Open suggestion creation dialog with pre-filled data
              if (openSuggestionDialog) {
                openSuggestionDialog(activityData);
                // Switch to Suggestions tab to show the newly created suggestion
                setCurrentTab('suggestions');
              }
            }}
          />
        </Box>

        {/* Add Note Dialogs */}
        {Object.entries(noteDialogs).map(([itemId, isOpen]) => {
          const item = itinerary.find(entry => entry.id.toString() === itemId);
          return (
            <Dialog
              key={itemId}
              open={isOpen}
              onClose={() => setNoteDialogs(prev => ({ ...prev, [itemId]: false }))}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: '3px',
                  bgcolor: DOCUMENT.surface,
                  color: DOCUMENT.ink,
                }
              }}
            >
              <DialogTitle
                sx={{
                  fontFamily: DOCUMENT.display,
                  fontWeight: 400,
                  fontSize: 23,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                {t('itinerary.addNote')}{item?.name ? ` · ${item.name}` : ''}
              </DialogTitle>

              <DialogContent sx={{ p: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('itinerary.notes')}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  autoFocus
                  sx={{
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: DOCUMENT.canvas,
                      '& fieldset': { borderColor: DOCUMENT.ruleHard },
                      '&:hover fieldset': { borderColor: DOCUMENT.accent },
                      '&.Mui-focused fieldset': { borderColor: DOCUMENT.accent },
                    },
                  }}
                />
              </DialogContent>

              <DialogActions sx={{
                px: 3,
                py: 1.5,
                gap: 2.5,
                justifyContent: 'flex-start',
                borderTop: `1px solid ${DOCUMENT.rule}`,
              }}>
                <Button
                  onClick={() => setNoteDialogs(prev => ({ ...prev, [itemId]: false }))}
                  sx={documentTextButtonSx}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={() => handleAddNote(itemId)}
                  disabled={!newNote.trim()}
                  sx={{
                    ml: 'auto',
                    background: DOCUMENT.oxblood,
                    color: DOCUMENT.ivory,
                    borderRadius: '2px',
                    fontSize: 12,
                    letterSpacing: '0.09em',
                    textTransform: 'uppercase',
                    px: 2.5,
                    py: 1.25,
                    '&:hover': { background: DOCUMENT.nav },
                    '&:disabled': { background: DOCUMENT.ruleHard, color: DOCUMENT.ink3 },
                  }}
                >
                  {t('itinerary.addNote')}
                </Button>
              </DialogActions>
            </Dialog>
          );
        })}

        {/* Add/Edit Dialog */}
        <ActivityModal
          open={dialogOpen} 
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
          mode={editingItem ? 'edit' : 'create'}
          initialData={formData}
          tripMembers={[]}
          submitting={submitting}
          submitError={actionError}
          onClearSubmitError={() => setActionError(null)}
          showVisibility={false}
          defaultVisibility="group"
          trip={trip}
          usePartnerAuth={Boolean(isPartner)}
        />

        <Dialog open={Boolean(photoItem)} onClose={() => setPhotoItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('itinerary.itemPhoto')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('itinerary.itemPhotoHint')}
            </Typography>
            {photoItem && (
              <SingleImageUploader
                value={photoItem.image_url || ''}
                onChange={handleChangeItemPhoto}
                preset="itinerary"
                folder="itinerary"
                label={t('itinerary.addItemPhoto')}
                height={180}
                cropShape="rect"
                usePartnerAuth={Boolean(isPartner)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPhotoItem(null)}>{t('itinerary.donePhoto')}</Button>
          </DialogActions>
        </Dialog>

        <ItemDetailModal
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          item={detailItem}
          canEdit={detailItem ? canEditItem(detailItem) : false}
          onEdit={handleEditFromDetailModal}
          onDelete={handleDeleteFromDetailModal}
          onMoveToPersonal={
            detailItem && canMoveItemToPersonal(detailItem)
              ? () => openMoveToPersonal(detailItem)
              : undefined
          }
          getCategoryIcon={(category, transportationType) => {
            if (category === 'hotel') return <HotelIcon />;
            if (transportationType === 'flight') return <FlightIcon />;
            if (category === 'transport') return <DirectionsCarIcon />;
            return <EventIcon />;
          }}
          getCurrencySymbol={getCurrencySymbol}
          generateGoogleMapsLink={generateGoogleMapsLink}
        />

        <Dialog
          open={movePersonalOpen}
          onClose={() => !movePersonalBusy && setMovePersonalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('itinerary.moveToPersonalTitle')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2, fontSize: 14, color: 'text.secondary' }}>
              {t('itinerary.moveToPersonalConfirm')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="move-personal-user-label">{t('itinerary.moveToPersonalPerson')}</InputLabel>
              <Select
                labelId="move-personal-user-label"
                label={t('itinerary.moveToPersonalPerson')}
                value={movePersonalUserId}
                onChange={(e) => setMovePersonalUserId(e.target.value)}
                disabled={movePersonalBusy || (!isPartner && !isCreator)}
              >
                {movePersonalMembers
                  .filter((m) => m.user_id)
                  .map((m) => (
                    <MenuItem key={m.user_id} value={m.user_id}>
                      {m.username || m.email || `User ${m.user_id}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMovePersonalOpen(false)} disabled={movePersonalBusy}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="contained"
              onClick={confirmMoveToPersonal}
              disabled={movePersonalBusy || !movePersonalUserId}
              data-testid="confirm-move-to-personal"
            >
              {t('itinerary.detailModal.moveToPersonal')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      
      {/* Itinerary Import Dialog */}
      <ItineraryImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        tripId={tripId}
        onImportComplete={handleImportComplete}
      />

      {/* ICS Import Dialog */}
      <ICSImportDialog
        open={icsImportDialogOpen}
        onClose={() => setIcsImportDialogOpen(false)}
        trip={trip}
        onSuccess={handleImportComplete}
      />

      {/* Email Import Dialog */}
      <EmailImportDialog
        open={emailImportOpen}
        onClose={() => setEmailImportOpen(false)}
        tripId={tripId}
        onImportComplete={handleImportComplete}
      />

      <Dialog
        open={optimizeDayOpen}
        onClose={() => !optimizeDayApplying && setOptimizeDayOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('itinerary.optimizeDayTitle')}</DialogTitle>
        <DialogContent>
          {optimizeDayLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {optimizeDayError ? (
                <Alert severity="error" sx={{ mb: 2 }}>{optimizeDayError}</Alert>
              ) : null}
              {optimizeDayProposal ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('itinerary.optimizeDayBody')}
                  </Typography>
                  {optimizeDayProposal.changed ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {t('itinerary.optimizeSaved', {
                        before: formatDurationMinutes(optimizeDayProposal.beforeSeconds),
                        after: formatDurationMinutes(optimizeDayProposal.afterSeconds),
                        saved: formatDurationMinutes(optimizeDayProposal.savedSeconds),
                      })}
                    </Alert>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t('itinerary.optimizeNoChange')}
                    </Alert>
                  )}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('itinerary.optimizeProposedOrder')}
                  </Typography>
                  <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                    {(optimizeDayProposal.items || []).map((item) => (
                      <Typography component="li" key={item.id} variant="body2" sx={{ mb: 0.5 }}>
                        {item.name}
                      </Typography>
                    ))}
                  </Box>
                </>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptimizeDayOpen(false)} disabled={optimizeDayApplying}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyOptimizeDay}
            disabled={
              optimizeDayLoading
              || optimizeDayApplying
              || !optimizeDayProposal?.changed
            }
          >
            {optimizeDayApplying ? t('common.saving') : t('itinerary.optimizeApply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        maxWidth="sm"
                  fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Delete Itinerary Item</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this itinerary item? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
              Cancel
            </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Road Trip Planner Dialog (from itinerary) */}
        <Dialog
          open={roadTripPlannerOpen}
          onClose={handleCloseRoadTripPlanner}
          maxWidth="lg"
          fullWidth
          disableScrollLock
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: { xs: '95vh', sm: '90vh' },
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              pr: 1.5,
              pt: 2,
              pb: 1.5,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.35, pr: 1 }}>
              {roadTripAnchorDate
                ? t('itinerary.planRoadTripFor', {
                  date: format(roadTripAnchorDate, 'EEEE, MMMM d', { locale: dateLocale }),
                })
                : t('itinerary.planRoadTrip')}
            </Box>
            <IconButton
              aria-label={t('common.close')}
              onClick={handleCloseRoadTripPlanner}
              edge="end"
              size="small"
              sx={{
                mt: -0.25,
                color: 'text.secondary',
                flexShrink: 0,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 }, overflowY: 'auto' }}>
            {roadTripPlannerOpen && trip && (
              <RoadTripPlanner
                compact
                itineraryMode
                targetTripId={Number(tripId)}
                tripStartDate={trip.start_date?.split('T')[0] || trip.start_date}
                tripEndDate={trip.end_date?.split('T')[0] || trip.end_date}
                initialAnchorDate={roadTripAnchorDate}
                onSaveComplete={handleRoadTripSaved}
                usePartnerAuth={Boolean(isPartner)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Road Trip Map Modal */}
        <RoadTripMapModal
          open={roadTripMapOpen}
          onClose={() => { setRoadTripMapOpen(false); setRoadTripMapData(null); }}
          roadTripData={roadTripMapData}
          itemName={roadTripMapItemName}
          tripId={roadTripMapTripId || tripId}
          tripStartDate={trip?.start_date?.split('T')[0] || trip?.start_date}
          tripEndDate={trip?.end_date?.split('T')[0] || trip?.end_date}
          canEdit={canEditMasterItinerary && !roadTripMapReadOnly}
          usePartnerAuth={Boolean(isPartner)}
          onEditComplete={() => {
            setRoadTripMapOpen(false);
            setRoadTripMapData(null);
            fetchItinerary();
          }}
        />

        {/* Publish as Travel Guide Dialog */}
        {isCreator && (
          <PublishGuideDialog
            open={publishGuideOpen}
            onClose={() => setPublishGuideOpen(false)}
            trip={trip}
            tripId={tripId}
          />
        )}
    </Container>
  );
});

export default TripItinerary; 
